import { SlideDrawer } from "@/components/ui/slide-drawer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DecisionDefinition } from "@/types/decision-definition-schema";
import { useState, useEffect } from "react";

interface DecisionSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  definition: DecisionDefinition;
  onChange: (definition: DecisionDefinition) => void;
  readOnly?: boolean;
}

export function DecisionSettingsDrawer({
  isOpen,
  onClose,
  definition,
  onChange,
  readOnly = false,
}: DecisionSettingsDrawerProps) {
  const [strictTyping, setStrictTyping] = useState(
    definition.settings?.strictTyping || false
  );
  const [traceEnabled, setTraceEnabled] = useState(
    definition.settings?.traceEnabledByDefault !== false
  );
  const [missingValuePolicy, setMissingValuePolicy] = useState(
    definition.settings?.defaultMissingValuePolicy || "unknown"
  );

  useEffect(() => {
    if (isOpen) {
      setStrictTyping(definition.settings?.strictTyping || false);
      setTraceEnabled(definition.settings?.traceEnabledByDefault !== false);
      setMissingValuePolicy(
        definition.settings?.defaultMissingValuePolicy || "unknown"
      );
    }
  }, [isOpen, definition]);

  const handleSave = () => {
    onChange({
      ...definition,
      settings: {
        ...definition.settings,
        strictTyping,
        traceEnabledByDefault: traceEnabled,
        defaultMissingValuePolicy:
          missingValuePolicy as DecisionDefinition["settings"]["defaultMissingValuePolicy"],
      },
    });
    onClose();
  };

  return (
    <SlideDrawer isOpen={isOpen} onClose={onClose}>
        <div className="px-6 pt-6 pb-2 border-b border-slate-100">
          <h2 className="text-lg font-semibold leading-none tracking-tight">Decision Settings</h2>
          <p className="text-sm text-muted-foreground mt-1.5">
            Configure evaluation behaviour (decision mode is set on the Decision node)
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="space-y-2">
            <Label>Missing Value Policy</Label>
            <Select
              value={missingValuePolicy}
              onValueChange={(v) => setMissingValuePolicy(v as typeof missingValuePolicy)}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unknown">Treat as Unknown</SelectItem>
                <SelectItem value="fail">Fail</SelectItem>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="null">Treat as Null</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="strict-typing">Strict Typing</Label>
            <Switch
              id="strict-typing"
              checked={strictTyping}
              onCheckedChange={setStrictTyping}
              disabled={readOnly}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="trace-enabled">Trace Enabled by Default</Label>
            <Switch
              id="trace-enabled"
              checked={traceEnabled}
              onCheckedChange={setTraceEnabled}
              disabled={readOnly}
            />
          </div>
        </div>
        <div className="border-t px-6 py-4">
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={readOnly}>
              Save Settings
            </Button>
          </div>
        </div>
    </SlideDrawer>
  );
}
